<template>
    <div v-if="show">
        <TransitionRoot as="template" :show="show">
            <Dialog as="div" class="relative z-50" @close="$emit('close')">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div>
                                    <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-50 border border-green-200">
                                        <font-awesome-icon :icon="faArrowUpRightFromSquare" class="h-6 w-6 text-green-700" aria-hidden="true" />
                                    </div>
                                    <div class="mt-3 text-center sm:mt-5">
                                        <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">{{ title }}</DialogTitle>
                                        <div class="mt-2">
                                            <template v-if="!linkOnly">
                                                <p class="text-sm text-gray-500">Click on one of the social media icons below to post it right away.</p>
                                                <div class="flex space-x-4 my-4 items-center justify-center">
                                                    <!-- Facebook Share Button -->
                                                    <button
                                                        v-if="showFacebook"
                                                        @click="shareToFacebook"
                                                        class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition flex flex-col items-center"
                                                    >
                                                        <font-awesome-icon :icon="faFacebook" size="lg" />
                                                        Facebook
                                                    </button>
                                                    <!-- Twitter/X Share Button -->
                                                    <button
                                                        @click="shareToTwitter"
                                                        class="bg-blue-400 text-white px-4 py-2 rounded hover:bg-blue-500 transition flex flex-col items-center"
                                                    >
                                                        <font-awesome-icon :icon="faXTwitter" size="lg" />
                                                        X/Twitter
                                                    </button>
                                                    <!-- LinkedIn Share Button -->
                                                    <button
                                                        @click="shareToLinkedIn"
                                                        class="bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition flex flex-col items-center"
                                                    >
                                                        <font-awesome-icon :icon="faLinkedinIn" size="lg" />
                                                        LinkedIn
                                                    </button>
                                                    <!-- Instagram Share Button -->
                                                    <button
                                                        v-if="showInstagram"
                                                        @click="shareToInstagram"
                                                        class="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 transition flex flex-col items-center"
                                                    >
                                                        <font-awesome-icon :icon="faInstagram" size="lg" />
                                                        Instagram
                                                    </button>
                                                </div>
                                                <p class="text-sm text-gray-500">Copy your link and share with your network.</p>
                                            </template>
                                            <p v-else class="text-sm text-gray-500">To start tenant screening, copy this link and share it with interested rental applicants.</p>

                                            <blockquote id="socialMediaShareLink"
                                                @click.stop="copySocialMediaShareLink()"
                                                :class="[
                                                    showCopyNotificationLinkOnly ? 'bg-green-50 border-green-600' : 'bg-gray-50 border-gray-200',
                                                    ' px-6 py-3 mt-3 rounded border break-words whitespace-pre-wrap text-left relative hover:cursor-pointer'
                                                ]"
                                            >
                                                <font-awesome-icon
                                                    :icon="faArrowUpRightFromSquare"
                                                    class="svg-inline--fa fa-arrow-up-right-from-square inline h-4 w-4 absolute top-0 right-0 p-2 bg-rent-risk-blue-400 text-white rounded"
                                                />
                                                <div v-if="showCopyNotificationLinkOnly" class="text-green-700 absolute w-full h-full justify-center items-center bg-gray-100 left-0 top-0 rounded flex">Link copied to clipboard!</div>
                                                {{ shareUrl }}
                                            </blockquote>
                                        </div>
                                    </div>
                                </div>
                                <div class="hidden fixed top-0 left-1/2 transform -translate-x-1/2 mt-4 w-full max-w-md px-4">
                                    <div class="bg-green-50 border border-gray-200 rounded-md shadow-md p-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0">
                                                <font-awesome-icon :icon="faCheckCircle" class="h-5 w-5 text-green-400" />
                                            </div>
                                            <div class="ml-3">
                                                <p class="text-sm font-medium text-green-800">
                                                    {{ copyNotificationMessage }}
                                                </p>
                                            </div>
                                            <div class="ml-auto pl-3">
                                                <button @click="showCopyNotification = false" class="text-gray-400 hover:text-gray-500">
                                                    <font-awesome-icon :icon="faXmark" class="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-6 flex justify-end gap-2">
                                    <!-- <button type="button" class="rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click.stop="copySocialMediaShareText()">Copy</button> -->
                                    <button type="button" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" @click.stop="$emit('close')">Close</button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons"
import { faCheckCircle, faXmark } from "@fortawesome/pro-light-svg-icons"
import { faFacebook, faInstagram, faLinkedinIn, faXTwitter } from '@fortawesome/free-brands-svg-icons'
import { recordOnboardingTask, SHARE_LINK_TASK } from '@/Shared/onboardingTasks'

export default {
    name: 'ShareModal',
    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        FontAwesomeIcon
    },
    props: {
        show: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Share Property Application Link'
        },
        shareUrl: {
            type: String,
            required: true
        },
        shareText: {
            type: String,
            required: true
        },
        showFacebook: {
            type: Boolean,
            default: true
        },
        showInstagram: {
            type: Boolean,
            default: false
        },
        linkOnly: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'onboarding-task-recorded'],
    data() {
        return {
            faArrowUpRightFromSquare,
            faCheckCircle,
            faXmark,
            faFacebook,
            faXTwitter,
            faLinkedinIn,
            faInstagram,
            showCopyNotification: false,
            showCopyNotificationLinkOnly: false,
            copyNotificationMessage: '',
            copyNotificationType: 'success'
        }
    },
    methods: {
        shareToFacebook() {
            // Facebook has limited support for pre-filled text due to platform policies
            // The sharer only accepts URL - users must add their own text
            const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.shareUrl)}`;
            window.open(url, '_blank', 'width=600,height=400');
            // Note: Facebook intentionally doesn't allow pre-filling post text to prevent spam
        },
        shareToTwitter() {
            const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.shareUrl)}&text=${encodeURIComponent(this.shareText)}`;
            window.open(url, '_blank', 'width=600,height=400');
        },
        shareToLinkedIn() {
            // LinkedIn only supports URL parameter now, so we'll open the feed share with a pre-composed message
            // Using the feed share endpoint which allows users to add their own text
            const shareMessage = `${this.shareText} - ${this.shareUrl}`;
            const url = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareMessage)}`;
            window.open(url, '_blank', 'width=600,height=400');
        },
        shareToInstagram() {
            // Instagram doesn't have a web share API - copy link to clipboard first, then open Instagram
            this.copyToClipboard(this.shareUrl);
            // Open Instagram web - users will need to create a post manually and paste the link
            const url = 'https://www.instagram.com/';
            window.open(url, '_blank', 'width=600,height=400');
            // Note: Instagram requires manual post creation - link has been copied to clipboard
        },
        async copySocialMediaShareText(text) {
            const shareText = document.getElementById('socialMediaShareText').innerText;
            await this.copyToClipboard(shareText, 'text');
        },
        async copySocialMediaShareLink() {
            await this.copyToClipboard(this.shareUrl,'url');
        },
        async copyToClipboard(text, type) {
            try {
                await navigator.clipboard.writeText(text);
                if(type === 'text') {
                    this.showCopyNotification = true;
                }
                else {
                    this.showCopyNotificationLinkOnly = true;
                }
                this.copyNotificationType = 'success';
                this.copyNotificationMessage = 'Copied to clipboard!';
                
                // Hide the notification after 3 seconds
                setTimeout(() => {
                    this.showCopyNotification = false;
                    this.showCopyNotificationLinkOnly = false;
                }, 3000);
            } catch($e) {
                if(type === 'text') {
                    this.showCopyNotification = true;
                }
                else {
                    this.showCopyNotificationLinkOnly = true;
                }
                this.copyNotificationType = 'error';
                this.copyNotificationMessage = 'Failed to copy to clipboard';
                
                // Hide the notification after 3 seconds
                setTimeout(() => {
                    this.showCopyNotification = false;
                    this.showCopyNotificationLinkOnly = false;
                }, 3000);
            }

            // 86bc2cgxd: any screening-link copy ticks the onboarding task — even a failed
            // copy, or a blocked clipboard would trap the landlord on the checklist.
            if (this.$page.props.onboarding?.active) {
                this.$emit('onboarding-task-recorded', recordOnboardingTask(SHARE_LINK_TASK));
            }
        }
    }
}
</script>